import { Component, Input } from '@angular/core';
import { Price, PRICE_TYPE } from '../../models/price';
import { OrdinalPipe } from '../pipes/ordinal.pipe';
import { HlCurrencyPipe } from '../pipes/hl-currency.pipe';
import { NgIf, NgFor, AsyncPipe, CurrencyPipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';

@Component({
  selector: 'prices-display',
  template: `
    <div class="flex flex-row flex-wrap gap-x-4">
      <ng-container *ngIf="type === 'standard'" ; else sequential>
        <p *ngFor="let price of _standardPrices">
          <strong
            ><span>{{ price.label }}: </span></strong
          >
          <span *ngFor="let value of price.values; let last = last"
            >{{ value.value | hlCurrency: value.currency | async }}
            {{ last === false ? '/ ' : '' }}
          </span>
        </p>
      </ng-container>
      <ng-template #sequential>
        <p
          *ngFor="let price of _sequentialPrices"
          class="flex flex-row gap-x-1"
        >
          <strong
            ><span>{{ price.label }}:</span></strong
          >
          <span>{{ price.value | currency: price.currency }}</span>
        </p>
      </ng-template>
    </div>
  `,
  providers: [OrdinalPipe],
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe, CurrencyPipe, HlCurrencyPipe]
})
export class PricesDisplayComponent {
  @Input() set prices(value: Price[]) {
    if (value && value.length) {
      this._prices = value;
      this.type = value[0].type;
      this.buildPrices();
    }
  }

  _sequentialPrices: { label: string; value: number; currency: string }[] = [];
  _standardPrices: {
    label: string;
    key: string;
    values: { value: number; currency: string }[];
  }[] = [];

  type: PRICE_TYPE = PRICE_TYPE.STANDARD;

  private _prices: Price[];

  constructor(private ordinalPipe: OrdinalPipe) {}

  private buildPrices() {
    if (!this._prices) {
      return;
    }
    if (this.type === PRICE_TYPE.STANDARD) {
      this.setStandardPrices(
        this._prices.filter((price) => price.type === PRICE_TYPE.STANDARD)
      );
    }
    if (this.type === PRICE_TYPE.SEQUENTIAL) {
      this.setSequentialPrices(
        this._prices.filter((price) => price.type === PRICE_TYPE.SEQUENTIAL)
      );
    }
  }

  private setStandardPrices(prices: Price[]) {
    const pricesGroupedByKey = prices
      .map(({ unit, sequence, ...others }) => ({
        ...others,
        unit,
        sequence,
        key: `${unit}${sequence ? sequence : ''}`
      }))
      .reduce(
        (acc, { key, value, currency_code }) => {
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push({ value, currency: currency_code ?? null });
          return acc;
        },
        {} as { [key: string]: { value: number; currency: string }[] }
      );
    const labelsMap = {
      day: 'First day',
      'day*': 'Add. day',
      week: 'Week',
      weekend: 'Weekend',
      month: 'Month'
    };
    const keyOrder = ['day', 'day*', 'week', 'weekend', 'month'];
    this._standardPrices = Object.entries(pricesGroupedByKey)
      .map(([key, values]) => ({
        key,
        label: labelsMap[key],
        values
      }))
      .sort((a, b) => keyOrder.indexOf(a.key) - keyOrder.indexOf(b.key));
  }

  private fillDefaultCurrencyAndSortByCurrency(prices: Price[]): Price[] {
    const currencies = Array.from(
      new Set(prices.map((price) => price.currency_code).filter(Boolean))
    );
    return prices
      .map(({ currency_code, ...others }) => ({
        currency_code: currency_code ?? null,
        ...others
      }))
      .sort(
        ({ currency_code: a }, { currency_code: b }) =>
          currencies.indexOf(a) - currencies.indexOf(b)
      );
  }

  private setSequentialPrices(prices: Price[]) {
    prices = this.fillDefaultCurrencyAndSortByCurrency(prices);
    const additionals = prices.filter((price) => price.sequence === '*');
    const sequentials = prices
      .filter((price) => price.sequence !== '*')
      .sort(
        (priceA, priceB) =>
          parseInt(priceA.sequence, 10) - parseInt(priceB.sequence, 10)
      );
    this._sequentialPrices = sequentials.map((price) => {
      let label = this.ordinalPipe.transform(parseInt(price.sequence, 10) + 1);
      const usePlural = price.multiplier > 1;
      label += usePlural
        ? ` ${price.multiplier} ${price.unit}s`
        : ` ${price.unit}`;
      return { label, value: price.value, currency: price.currency_code };
    });
    if (additionals.length) {
      const usePlural = additionals[0].multiplier > 1;
      let label = `Each additional`;
      label += usePlural
        ? `${additionals[0].multiplier} ${additionals[0].unit}s`
        : ` ${additionals[0].unit}`;
      this._sequentialPrices.push({
        label,
        value: additionals[0].value,
        currency: additionals[0].currency_code
      });
    }
  }
}
